import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  PencilIcon,
  TrashIcon,
  InformationCircleIcon,
  EyeIcon,
} from "@heroicons/react/24/solid";
import { UploadFileModal } from "./ui/UploadFileModal";
import { EnhancedUploadFileModal } from "./ui/EnhancedUploadFileModal";
import { EnhancedInstructionsModal } from "./ui/EnhancedInstructionsModal";
import { InfoModal } from "./ui/InfoModal";
import { DeleteConfirmModal } from "./ui/DeleteConfirmModal";
import { PublishConfirmModal } from "./ui/PublishConfirmModal";
import { EditDescriptionModal } from "./ui/EditDescriptionModal";

// Services and utilities
import fileRelationService from "../services/fileRelationService";
import fileService from "../services/fileService";
import vectorService from "../services/vectorService";
import collectionsService from "../services/collectionsService";
import { useCollection } from "../contexts/CollectionContext";
import { useDashboardUI } from "../context/GlobalContext";
import { transformFileDetailsToDisplayData } from "../helpers/dataTransform";
import { formatDate } from "../helpers/fileUtils";
import { previewRagFile, displayUserName } from "../helpers/helpers";

export const CollectionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    getCollectionName,
    getCollectionData,
    updateCollectionData,
    fetchCollectionDetails,
  } = useCollection();
  const { facilityUserList } = useDashboardUI();

  // State management
  const [files, setFiles] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState({
    show: false,
    fileData: null,
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [isPublished, setIsPublished] = useState(false); // Now properly initialized
  const [editFileId, setEditFileId] = useState(null);
  const [isEditingFile, setIsEditingFile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isTogglingPublish, setIsTogglingPublish] = useState(false); // New loading state
  const [collectionDetails, setCollectionDetails] = useState(null);
  const [isUpdatingInstructions, setIsUpdatingInstructions] = useState(false);
  const [isDeletingFile, setIsDeletingFile] = useState(null); // File ID being deleted
  const [previewingFile, setPreviewingFile] = useState(null); // blob_url being previewed

  // Collection name from context with fallback
  const collectionName = getCollectionName(id);

  // Comprehensive data loading function
  const loadCollectionAndFiles = async (bustCache = false) => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    try {
      // Step 1: Fetch collection details first
      const collectionData = await fetchCollectionDetails(id);
      setCollectionDetails(collectionData);

      // Update publish status from fetched collection data
      if (typeof collectionData.is_published === "boolean") {
        setIsPublished(collectionData.is_published);
      }

      // Step 2: Get file relations
      const fileRelations = await fileRelationService.getFileRelations(
        id,
        bustCache
      );

      if (!fileRelations || fileRelations.length === 0) {
        setFiles([]);
        return;
      }

      // Step 3: Extract file IDs
      const fileIds = fileRelations
        .map((relation) => relation.file_id)
        .filter(Boolean);

      if (fileIds.length === 0) {
        setFiles([]);
        return;
      }

      // Step 4: Get detailed file information
      const fileDetailsResponse = await fileService.getFileDetailsByIds(
        fileIds
      );

      // Step 5: Transform to display data
      const displayData = transformFileDetailsToDisplayData(
        fileDetailsResponse,
        facilityUserList
      );
      setFiles(displayData);

      // Step 6: Handle partial failures
      if (
        fileDetailsResponse.metadata &&
        fileDetailsResponse.metadata.not_found_ids.length > 0
      ) {
        const notFoundCount = fileDetailsResponse.metadata.not_found_ids.length;
        const totalCount = fileDetailsResponse.metadata.total_requested;
        toast.error(
          `${notFoundCount} out of ${totalCount} files could not be loaded`
        );
      }
    } catch (err) {
      console.error("Error loading collection and files:", err);
      if (err.message && err.message.includes("collection")) {
        // Collection details fetch failed
        toast.error("Failed to load collection details");
        setError("Failed to load collection details. Please try again.");
      } else {
        // Files fetch failed
        toast.error("Failed to load files");
        setError("Failed to load files. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Load collection and files on component mount and when collection ID changes.
  // Note: we intentionally do NOT refetch on window focus — switching browser
  // tabs and returning should not re-run the whole details/file-relations chain.
  // The list already refreshes after uploads/edits/deletes via loadCollectionAndFiles.
  useEffect(() => {
    loadCollectionAndFiles();
  }, [id]);

  // Filtered and paginated files
  const filteredFiles = files.filter((f) =>
    f.name.toLowerCase().includes(search.toLowerCase())
  );
  const total = filteredFiles.length;
  const totalPages = Math.ceil(total / rowsPerPage);
  const pagedFiles = filteredFiles.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const handleRowsPerPage = (value) => {
    setRowsPerPage(Number(value));
    setPage(1);
  };

  const handleUploadComplete = async () => {
    // Refresh files list after successful upload
    try {
      // Add a small delay to ensure backend processing is complete
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Refresh the files list with cache busting
      await loadCollectionAndFiles(true);

      // Note: Success toast and modal closing now handled by the modal's "Done" button
    } catch (error) {
      console.error("Error refreshing files after upload:", error);
      toast.warning("Please refresh the page to see the latest files");
      // Note: Modal closing now handled by the modal's "Done" button
    }
  };

  /**
   * Handle file description and name edit
   */
  const handleEditFileDescription = async (newDescription, newName) => {
    if (!editFileId) return;

    setIsEditingFile(true);

    try {
      // Call API to update file description and name
      const updatedFile = await fileService.updateFileDescription(
        editFileId,
        newDescription,
        newName
      );

      // Update local state with the new description and name
      setFiles((prevFiles) =>
        prevFiles.map((file) =>
          file.id === editFileId
            ? { ...file, description: newDescription, filename: newName }
            : file
        )
      );

      // Refresh the files list to ensure consistency
      await loadCollectionAndFiles(true);

      toast.success("File description updated successfully!");
      setEditFileId(null);
    } catch (error) {
      console.error("Error updating file description:", error);
      toast.error("Failed to update file description");
    } finally {
      setIsEditingFile(false);
    }
  };

  /**
   * Handle publish status toggle
   */
  const handlePublishToggle = async () => {
    if (!id) return;

    const newPublishStatus = !isPublished;
    const previousPublishStatus = isPublished;

    setIsTogglingPublish(true);
    setShowPublishConfirm(false);

    try {
      // Optimistically update UI
      setIsPublished(newPublishStatus);

      // Call API to toggle publish status
      const updatedCollection = await collectionsService.togglePublishStatus(
        id,
        newPublishStatus
      );

      // Update context with the response data
      updateCollectionData(id, updatedCollection);

      // Show success toast
      toast.success(
        `Collection ${
          newPublishStatus ? "published" : "unpublished"
        } successfully!`
      );
    } catch (error) {
      console.error("Error toggling publish status:", error);

      // Revert state on error
      setIsPublished(previousPublishStatus);

      // Show error toast
      toast.error(
        `Failed to ${
          newPublishStatus ? "publish" : "unpublish"
        } collection. Please try again.`
      );
    } finally {
      setIsTogglingPublish(false);
    }
  };

  /**
   * Handle instructions update
   */
  const handleInstructionsSubmit = async (newInstructions) => {
    if (!id || !collectionDetails) return;

    setIsUpdatingInstructions(true);

    try {
      // Prepare update data with all required fields
      const updateData = {
        name: collectionDetails.name,
        description: collectionDetails.description,
        prompt_instructions: newInstructions,
      };

      console.log("🔍 INSTRUCTIONS UPDATE - Update data:", updateData);

      // Call API to update collection
      const updatedCollection = await collectionsService.updateCollection(
        id,
        updateData
      );

      console.log(
        "🔍 INSTRUCTIONS UPDATE - Updated collection response:",
        updatedCollection
      );

      // Update local state
      setCollectionDetails((prev) => ({
        ...prev,
        prompt_instructions: newInstructions,
      }));

      // Update context with the response data
      updateCollectionData(id, updatedCollection);

      console.log(
        "🔍 INSTRUCTIONS UPDATE - Current files state before:",
        files
      );

      // IMPORTANT: Refresh files data to sync with backend state
      await loadCollectionAndFiles(true);

      console.log("🔍 INSTRUCTIONS UPDATE - Refreshed files data");

      // Show success toast
      toast.success("Instructions updated successfully");
      setShowInstructionsModal(false);
    } catch (error) {
      console.error("Error updating instructions:", error);
      toast.error("Failed to update instructions");
    } finally {
      setIsUpdatingInstructions(false);
    }
  };

  /**
   * Handle file deletion (both file relation and file from system)
   */
  const handleFileDelete = async (fileId) => {
    if (isDeletingFile) return; // Prevent concurrent deletions

    setIsDeletingFile(fileId);
    const fileToDelete = files.find((f) => f.id === fileId);
    const currentPageFiles = pagedFiles.length;

    try {
      // Optimistic UI update
      setFiles((prev) => prev.filter((f) => f.id !== fileId));

      // Step 1: Delete file relation first
      await fileRelationService.deleteFileRelationByIds(fileId, id);

      // Step 2: Soft-delete the file's vectors from the search index. The
      // file-relation delete above only soft-deletes the DB row; without this
      // the collection's Qdrant chunks stay live and keep surfacing in
      // retrieval. Skip if the file has no blob URL (nothing was embedded).
      if (fileToDelete?.fileUrl) {
        await vectorService.softDeleteDocuments([
          { file_url: fileToDelete.fileUrl },
        ]);
      }

      // Step 3: Delete file from system
      await fileService.deleteFile(fileId);

      // Handle pagination if current page becomes empty
      if (currentPageFiles === 1 && page > 1) {
        setPage((prev) => prev - 1);
      }

      // Refresh to ensure consistency and show success
      try {
        await loadCollectionAndFiles(true);
        toast.success("File deleted successfully");
      } catch (refreshError) {
        console.error("Error refreshing after deletion:", refreshError);
        toast.success("File deleted successfully, but failed to refresh list");
      }
    } catch (error) {
      console.error("Error deleting file:", error);

      // Revert optimistic update
      setFiles((prev) => {
        const updatedFiles = [...prev, fileToDelete];
        return updatedFiles.sort((a, b) => a.name.localeCompare(b.name));
      });

      // Specific error handling
      if (error.message.includes("relation")) {
        toast.error("Failed to remove file from collection");
      } else if (error.message.includes("permission")) {
        toast.error("You don't have permission to delete this file");
      } else if (error.message.includes("not found")) {
        toast.error("File not found. It may have already been deleted");
      } else {
        toast.error("Failed to delete file. Please try again");
      }
    } finally {
      setIsDeletingFile(null);
      setShowDeleteConfirm(null);
    }
  };

  return (
    <div className="flex h-full w-full flex-col pt-4 pb-4">
      <div className="flex min-h-0 w-full flex-1 flex-col gap-4">
        {/* Collection Header Section */}
        <div className="rounded-2xl bg-white px-4 py-5 sm:px-6 shadow-[0_1px_3px_rgba(20,18,43,0.04),0_8px_24px_rgba(20,18,43,0.05)]">
        {isLoading && !collectionDetails ? (
          <div>
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-1"></div>
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            </div>
          </div>
        ) : collectionDetails ? (
          <div>
            <h2 className="text-2xl font-bold mb-2 text-[#14122B] break-words">{collectionDetails.name}</h2>
            <p className="text-gray-600 mb-1 break-words">
              {collectionDetails.description || "No description"}
            </p>
            <p className="text-sm text-gray-500">
              Created on {formatDate(collectionDetails.created_at)}
            </p>
          </div>
        ) : (
          <div>
            <h2 className="text-2xl font-bold text-[#14122B]">{collectionName}</h2>
          </div>
        )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Files toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <input
            type="text"
            placeholder="Search files..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full sm:w-72 h-10 sm:h-11 rounded-xl border border-[#ECEEF4] bg-white px-4 sm:px-5 text-sm text-gray-700 placeholder:text-[#9AA1B2] transition-colors hover:border-[#D9DCE6] focus:outline-none focus:border-[#8B5CF6]/50 focus:ring-2 focus:ring-[#8B5CF6]/10"
          />
          <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto">
            {/* Publish toggle */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-[#14122B]">Publish Collection:</span>
              <button
                className={`inline-flex h-8 items-center justify-center rounded-full px-4 text-xs font-medium ${
                  isPublished
                    ? "bg-success-green-100 text-success-green-800"
                    : "bg-gray-100 text-gray-600"
                }`}
                onClick={() => setShowPublishConfirm(true)}
                disabled={isTogglingPublish}
              >
                {isTogglingPublish
                  ? "Updating..."
                  : isPublished
                  ? "Published"
                  : "Unpublished"}
              </button>
            </div>
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 rounded-xl bg-[#3F4BB8] px-5 h-11 text-sm font-semibold text-white hover:bg-[#363FA0] transition"
            >
              Upload Files
            </button>
            <button
              onClick={() => setShowInstructionsModal(true)}
              className="flex items-center gap-2 rounded-xl bg-[#3F4BB8] px-5 h-11 text-sm font-semibold text-white hover:bg-[#363FA0] transition"
            >
              Add Instructions
            </button>
          </div>
        </div>

        {/* Table card (same look as the Org Collection table) */}
        <div className="flex-1 min-h-[240px] bg-white rounded-2xl overflow-hidden shadow-[0_1px_3px_rgba(20,18,43,0.04),0_8px_24px_rgba(20,18,43,0.05)]">
          <div className="table-scroll h-full overflow-auto">
          <table className="w-full min-w-[900px] xl:min-w-[800px] table-fixed border-collapse text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                {[
                  { label: "Name", width: "26%" },
                  { label: "Description", width: "28%" },
                  { label: "Date Created", width: "18%" },
                  { label: "Created By", width: "16%" },
                  { label: "Actions", width: "12%" },
                ].map(({ label, width }) => (
                  <th
                    key={label}
                    style={{ position: "sticky", top: 0, background: "#F5F2FF", zIndex: 20, width, textAlign: "left" }}
                    className="py-3.5 pl-6 pr-3 align-middle text-xs font-semibold text-gray-500 uppercase tracking-wide"
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-10 text-center text-sm text-gray-500"
                  >
                    Loading files...
                  </td>
                </tr>
              ) : pagedFiles.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-10 text-center text-sm text-gray-500"
                  >
                    {search
                      ? "No files match your search."
                      : "No files uploaded yet."}
                  </td>
                </tr>
              ) : (
                pagedFiles.map((file) => (
                  <tr
                    key={file.id}
                    className="border-b border-gray-100 last:border-0 transition-colors hover:bg-gray-50"
                  >
                    <td className="pl-6 pr-3 py-4 align-middle text-sm font-semibold text-[#14122B] truncate" title={file.name}>
                      {file.name}
                    </td>
                    <td className="pl-6 pr-3 py-4 align-middle text-gray-600 truncate" title={file.description}>
                      {file.description}
                    </td>
                    <td className="pl-6 pr-3 py-4 align-middle text-gray-600">
                      {file.dateCreated}
                    </td>
                    <td className="pl-6 pr-3 py-4 align-middle text-gray-600 truncate">
                      {displayUserName(facilityUserList, file.userId) ||
                        file.createdBy}
                    </td>
                    <td className="pl-6 pr-3 py-4 align-middle text-left">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="rounded-full p-2 hover:bg-gray-100"
                            disabled={isDeletingFile}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5 text-gray-500"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <circle cx="10" cy="4" r="1.5" />
                              <circle cx="10" cy="10" r="1.5" />
                              <circle cx="10" cy="16" r="1.5" />
                            </svg>
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="bg-white shadow-lg"
                        >
                          <DropdownMenuItem
                            onClick={() => setEditFileId(file.id)}
                            disabled={isDeletingFile}
                          >
                            <PencilIcon className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={previewRagFile(
                              file.fileUrl,
                              "file",
                              setPreviewingFile
                            )}
                            disabled={
                              isDeletingFile || previewingFile === file.fileUrl
                            }
                          >
                            <EyeIcon className="w-4 h-4 mr-2" />
                            {previewingFile === file.fileUrl
                              ? "Opening…"
                              : "Preview"}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setShowDeleteConfirm(file)}
                            disabled={isDeletingFile}
                          >
                            <TrashIcon className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              setShowInfoModal({ show: true, fileData: file })
                            }
                            disabled={isDeletingFile}
                          >
                            <InformationCircleIcon className="w-4 h-4 mr-2" /> Info
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>

        {/* Pagination footer */}
        <div className="flex flex-nowrap items-center justify-between gap-2 whitespace-nowrap text-sm">
          <span>Total {total}</span>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              Rows per page
              <select
                value={rowsPerPage}
                onChange={(e) => handleRowsPerPage(String(e.target.value))}
                className="rounded-md border px-2 py-1"
              >
                {[10, 20, 30].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-1 text-base leading-none sm:px-2.5 sm:py-0.5 sm:text-2xl disabled:opacity-40"
              >
                ‹
              </button>
              <span>
                {page} / {totalPages}
              </span>
              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-1 text-base leading-none sm:px-2.5 sm:py-0.5 sm:text-2xl disabled:opacity-40"
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>
      <EnhancedUploadFileModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadComplete={handleUploadComplete}
        collectionId={id}
        fileFacilityId={collectionDetails?.facility_id ?? null}
      />
      <EnhancedInstructionsModal
        open={showInstructionsModal}
        onClose={() => setShowInstructionsModal(false)}
        initialInstructions={collectionDetails?.prompt_instructions || ""}
        onSave={handleInstructionsSubmit}
        isLoading={isUpdatingInstructions}
      />
      <InfoModal
        open={showInfoModal.show}
        onClose={() => setShowInfoModal({ show: false, fileData: null })}
        file={showInfoModal.fileData}
      />
      <EditDescriptionModal
        open={!!editFileId}
        onClose={() => {
          setEditFileId(null);
          setIsEditingFile(false);
        }}
        initialDescription={
          editFileId
            ? files.find((f) => f.id === editFileId)?.description || ""
            : ""
        }
        initialName={
          editFileId
            ? files.find((f) => f.id === editFileId)?.filename || ""
            : ""
        }
        onSave={handleEditFileDescription}
        isLoading={isEditingFile}
      />
      <DeleteConfirmModal
        open={!!showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(null)}
        onDelete={() => handleFileDelete(showDeleteConfirm.id)}
        fileName={showDeleteConfirm?.filename || showDeleteConfirm?.name}
        isDeleting={isDeletingFile === showDeleteConfirm?.id}
      />
      <PublishConfirmModal
        open={showPublishConfirm}
        onClose={() => setShowPublishConfirm(false)}
        onConfirm={handlePublishToggle}
        isPublished={isPublished}
      />
    </div>
  );
};
